/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-25 17:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { TextStyle, ProgressBar, Card, SkeletonBodyText } from '@shopify/polaris';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import RefreshIcon from 'src/assets/images/common/refresh.svg';
import SyncRunningIcon from 'src/assets/images/common/sync.running.svg';
import SyncWarnIcon from 'src/assets/images/common/sync.warn.svg';
import SyncFailIcon from 'src/assets/images/common/sync.fail.svg';
import SyncStatusBanner from 'src/assets/images/banners/sync-status-banner.svg';

import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';

import { site } from 'src/config/';
import { formatDatetime } from 'src/helpers';
import { URL } from 'src/constants/links';
import { SYNC_TYPE_FLOW_MANUAL_SYNC, getSyncContent } from 'src/constants/sync';
import { SYNC_FREQUENCY_NAME } from 'src/constants/subscription';
import history from 'src/routes/history';

import * as syncActions from 'actions/sync-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import { routes } from 'src/routes';

type Props = {
  syncTypeOptions: Array<{ value: string, label: string }>,
  triggerSyncLoading: boolean,
  error: any,
  syncProcessLoading: boolean,
  flow: Object,
  syncProcess: Object,
  tenantId: string,
  availableSyncList: Array<string>,
  getSyncStatus: (tenantId: string) => void,
  getSyncProcessStatus: (flowName: string) => void,
  triggerSync: () => void
};

type State = {
  syncType: string,
  showProgress: boolean,
  triggerSync: boolean,
  percentage: number,
  triggerSyncFlag: boolean,
  activeConfirmSyncModal: boolean,
  activeCollectionNotFoundModal: boolean,
  syncProcessDot: string,
  changePlanFromReviewIOModal: boolean
};

type ComposedProps = Props & WithI18nProps;

class StatusSyncStatus extends Component<ComposedProps, State> {
  timeoutGetSyncProcessStatus = undefined;

  state = {
    syncType: SYNC_TYPE_FLOW_MANUAL_SYNC,
    showProgress: false,
    triggerSync: false,
    percentage: 0,
    triggerSyncFlag: false,
    activeConfirmSyncModal: false,
    activeCollectionNotFoundModal: false,
    syncProcessDot: '',
    modalAddon: false,
    changePlanFromReviewIOModal: false
  };

  componentDidMount() {
    const { getSyncProcessStatus } = this.props;
    getSyncProcessStatus();
    // getAddonStatus(SYNC_FREQUENCY_NAME);
  }

  componentWillReceiveProps(nextProps: Props) {
    const { syncProcessLoading } = nextProps;
    const { syncProcessLoading: syncProcessLoadingPrev } = this.props;
    // Check sync process: load to call api -> load done api
    if (syncProcessLoadingPrev && !syncProcessLoading) {
      const { flow, getSyncProcessStatus, availableSyncList } = nextProps;
      const { status, completedPercent = 0 } = flow;
      const { showProgress, syncType, triggerSyncFlag } = this.state;
      // Status = "done"
      if (status === 'done') {
        // showProgress = false: Do nothing, show button trigger
        // showProgress = true: Reset progress
        if (showProgress) {
          // Update percentage
          const percentage = parseFloat(completedPercent * 100);
          if (!Number.isNaN(percentage)) {
            this.setState({ percentage });
          }

          // Set time out 10s: showProgress = false
          setTimeout(() => {
            this.setState({ percentage: 0, showProgress: false });
          }, site.showSyncProgressTimeout);
        }

        // Update trigger sync flag
        if (triggerSyncFlag) {
          this.setState({ triggerSyncFlag: false });
        }
        // Update syncType if needed
        if (_.isEmpty(syncType) && !_.isEmpty(availableSyncList)) {
          this.setState({ syncType: SYNC_TYPE_FLOW_MANUAL_SYNC });
        }
      } else {
        // Status = "running" ||"error" || anything else --> show progress
        // Check to show progress
        if (!showProgress) {
          this.setState({
            showProgress: true,
            activeConfirmSyncModal: false,
            activeCollectionNotFoundModal: false
          });
        }

        // Update percentage
        const percentage = parseFloat(completedPercent * 100);
        if (!Number.isNaN(percentage)) {
          this.setState({ percentage });
        }

        // Update sync type
        const { flowName } = flow;
        if (syncType !== flowName && !_.isEmpty(availableSyncList)) {
          if (availableSyncList.includes(flowName)) {
            this.setState({ syncType: flowName });
          } else {
            this.setState({ syncType: SYNC_TYPE_FLOW_MANUAL_SYNC });
          }
        }
      }

      // Set time out to get sync status -- Always run
      this.timeoutGetSyncProcessStatus = setTimeout(() => {
        getSyncProcessStatus(syncType);
      }, site.syncStatusTimeout);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutGetSyncProcessStatus);
  }

  renderContentSync = input => {
    const { i18n, flow, percentage, syncProcess, enabledSubscriptionV4 } = input;
    const { status, createdAt, flowName } = flow;
    const percent = _.isInteger(percentage) ? percentage : percentage.toFixed(2);
    const syncContent = getSyncContent(flowName, i18n, enabledSubscriptionV4);

    let { processedCount = 0, totalCount = 0 } = syncProcess;
    const { processName = '' } = syncProcess;
    processedCount = _.isInteger(processedCount)
      ? processedCount
      : parseFloat(processedCount).toFixed(2);

    switch (status) {
      case 'wait-retry': {
        return (
          <>
            <div className="render-status-block">
              {SyncWarnIcon && (
                <div className={`b-icon ${status}`}>
                  <img src={SyncWarnIcon} width={28} height={28} alt="sync-icon" />
                </div>
              )}
              <div className="b-title">
                {i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_WaitRetry2', {
                  few: (
                    <b style={{ color: '#008060' }}>
                      {i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Error2_few')}.
                    </b>
                  )
                })}
              </div>
              {+percent !== 100 && flowName === 'FlowManualSync' && (
                <div className="b-progress">
                  <div className="b-percent">
                    {i18n.translate('AdminHTML.DashboardV3.SyncHookStatus.percentToCompleteLabel', {
                      nt_number: percent
                    })}
                  </div>
                  <div className={`b-progress-bar ${status}`}>
                    <ProgressBar progress={percent} />
                  </div>
                  <div className="b-partical">
                    <img src={RefreshIcon} alt="refresh" className="loading2" />
                    <TextStyle variation="strong">
                      {`${processName} - ${processedCount}/${totalCount}`}
                    </TextStyle>
                  </div>
                </div>
              )}
            </div>
          </>
        );
      }
      case 'error': {
        return (
          <>
            <div className="render-status-block">
              {SyncFailIcon && (
                <div className={`b-icon ${status}`}>
                  <img src={SyncFailIcon} width={28} height={28} alt="sync-icon" />
                </div>
              )}
              <div className="b-title">
                {i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Error2', {
                  few: (
                    <b style={{ color: '#008060' }}>
                      {i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Error2_few')}
                    </b>
                  )
                })}
              </div>
            </div>
          </>
        );
      }
      case 'running': {
        return (
          <>
            <div className="render-status-block">
              {SyncRunningIcon && (
                <div className={`b-icon ${status}`}>
                  <img src={SyncRunningIcon} width={28} height={28} alt="sync-icon" />
                </div>
              )}
              <div className="b-title">
                {_.isEmpty(flowName)
                  ? i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Default_AutoSync', {
                      nt_time: <span className="text-value">{formatDatetime(createdAt)}</span>
                    })
                  : i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Default_Sync_2', {
                      nt_process_type: syncContent.name,
                      nt_time: <span className="text-value">{formatDatetime(createdAt)}</span>
                    })}
              </div>

              {(flowName === 'FlowManualSync' || flowName === 'FlowB2B') && (
                <div className="b-progress">
                  <div className="b-percent">
                    {i18n.translate('AdminHTML.DashboardV3.SyncHookStatus.percentToCompleteLabel', {
                      nt_number: percent
                    })}
                  </div>
                  <div className={`b-progress-bar ${status}`}>
                    <ProgressBar progress={percent} />
                  </div>
                  <div className="b-partical">
                    <img src={RefreshIcon} alt="refresh" className="loading2" />
                    <TextStyle variation="strong">
                      {`${processName} - ${processedCount}/${totalCount}`}
                    </TextStyle>
                  </div>
                </div>
              )}

              <div className="note">
                {
                  <div style={{ marginTop: '4px', fontSize: '12px' }}>
                    {i18n.translate('AdminHTML.Sync.SyncStatus.syncRecentUpdateText', {
                      nt_process_type: syncContent.name?.toLowerCase()
                    })}
                  </div>
                }
              </div>
            </div>
          </>
        );
      }
      case 'done':
      default: {
        return (
          <>
            <div className="render-status-block">
              <div className="b-title">
                {_.isEmpty(flowName)
                  ? i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Default_AutoSync', {
                      nt_time: <span className="text-value">{formatDatetime(createdAt)}</span>
                    })
                  : i18n.translate('AdminHTML.Sync.Sync.renderCaptionProgress_Default_Sync_2', {
                      nt_process_type: syncContent.name,
                      nt_time: <span className="text-value">{formatDatetime(createdAt)}</span>
                    })}
              </div>

              {flowName === 'FlowManualSync' && (
                <div className="b-progress">
                  <div className="b-percent">
                    {i18n.translate('AdminHTML.DashboardV3.SyncHookStatus.percentToCompleteLabel', {
                      nt_number: percent
                    })}
                  </div>
                  <div className={`b-progress-bar ${status}`}>
                    <ProgressBar progress={percent} />
                  </div>
                  <div className="b-partical">
                    <img src={RefreshIcon} alt="refresh" className="loading2" />
                    <TextStyle variation="positive">
                      {i18n.translate('AdminHTML.Sync.Sync.getCurrentProcess_TextStyle')}
                    </TextStyle>
                  </div>
                </div>
              )}
            </div>
          </>
        );
      }
    }
  };

  renderLastSyncDoneTime = i18n => {
    const { syncType } = this.state;
    const { flow, enabledSubscriptionV4 } = this.props;
    const { flowName } = flow;

    if (
      flowName === syncType ||
      flowName === 'FlowFullSync' ||
      syncType === SYNC_TYPE_FLOW_MANUAL_SYNC
    ) {
      const { updatedAt } = flow;
      if (updatedAt) {
        return (
          <div className="recent-last-done">
            {i18n.translate('AdminHTML.Sync.Sync.renderLastSyncDoneTime', {
              nt_action: getSyncContent(flowName || syncType, i18n, enabledSubscriptionV4).name,
              nt_time: <span className="text-value">{formatDatetime(updatedAt)}</span>
            })}
          </div>
        );
      }
    }
    return '';
  };

  renderNextSyncScheduleName = (syncSchedule, i18n) => {
    const { enabledSubscriptionV4 } = this.props;
    const { flowName = '' } = syncSchedule;
    return getSyncContent(flowName, i18n, enabledSubscriptionV4)?.name?.toLowerCase();
  };

  render() {
    const { syncType, percentage } = this.state;
    let {
      i18n,
      syncSchedule,
      onOpenScheduleSync,
      syncProcess,
      getSyncHistoryLoading,
      getSyncScheduleLoading,
      flow,
      selectedPlan,
      enabledSubscriptionV4,
      renderNextSyncScheduleDate,
      useBoltOn
    } = this.props;
    let { status } = flow;

    const scheduleDate = syncSchedule
      ? renderNextSyncScheduleDate(syncSchedule?.scheduleAt || 0)
      : '';
    const scheduleName = syncSchedule ? this.renderNextSyncScheduleName(syncSchedule, i18n) : '';

    return (
      <>
        <Card sectioned title={i18n.translate('AdminHTML.Sync.SyncStatus.title')}>
          {status === 'done' && (
            <img className="bg" src={SyncStatusBanner} width={100} height={236} alt="bg" />
          )}
          <div
            className={`content ${status === 'done' ? 'content-center' : ''} ${
              enabledSubscriptionV4 ? 'content-v4' : ''
            }`}>
            {getSyncHistoryLoading && (
              <div style={{ maxWidth: '180px' }}>
                <SkeletonBodyText />
              </div>
            )}

            {!getSyncHistoryLoading &&
              (status === 'done' ? (
                this.renderLastSyncDoneTime(i18n, syncType)
              ) : (
                <>
                  {this.renderContentSync({
                    i18n,
                    syncType,
                    flow,
                    percentage,
                    syncProcess
                  })}
                </>
                // component make progress re-render
                // <SyncStat
                //   i18n={i18n}
                //   syncType={syncType}
                //   flow={flow}
                //   percentage={percentage}
                //   syncProcess={syncProcess}
                // />
              ))}

            {getSyncScheduleLoading && (
              <div style={{ maxWidth: '180px' }}>
                <SkeletonBodyText />
              </div>
            )}
            {!getSyncScheduleLoading && syncSchedule && scheduleDate !== '' && (
              <div className="next-schedule">
                {i18n.translate('AdminHTML.Sync.SyncStatus.nextSchedule', {
                  nt_process_type: scheduleName
                })}
                <span className="text-value">{scheduleDate}</span>
                <div className="edit-schedule" onClick={() => onOpenScheduleSync(true)}>
                  {i18n.translate('AdminHTML.Sync.SyncStatus.editScheduleSync')}
                </div>
              </div>
            )}
            {enabledSubscriptionV4 && (
              <div className="detail-sync-status">
                <div>{this.renderText(selectedPlan, i18n)}</div>
                {!selectedPlan.includes('professional') && (
                  <p
                    onClick={() =>
                      useBoltOn
                        ? this.setState({ changePlanFromReviewIOModal: true })
                        : history.push(routes.subscription.package())
                    }
                    className="upgrade">
                    {i18n.translate('AdminHTML.Sync.SyncStatus.requestUpgradeText')}
                  </p>
                )}
              </div>
            )}
          </div>
        </Card>
        <ChangePlanFromReviewIOModal
          open={this.state.changePlanFromReviewIOModal}
          onClose={() => this.setState({ changePlanFromReviewIOModal: false })}
        />
      </>
    );
  }

  renderText = (selectPlan = 'basic', i18n) => {
    if (selectPlan.includes('freemium')) {
      return i18n.translate('AdminHTML.Sync.SyncStatus.syncInfo03', {
        time: i18n.translate('AdminHTML.Sync.SyncStatus.syncInfo03_t1')
      });
    }
    if (selectPlan.includes('basic') || _.isEmpty(selectPlan) || !selectPlan) {
      return i18n.translate('AdminHTML.Sync.SyncStatus.syncInfo01', {
        number: 3
      });
    }
    if (selectPlan.includes('essential'))
      return i18n.translate('AdminHTML.Sync.SyncStatus.syncInfo01', {
        number: 7
      });

    return i18n.translate('AdminHTML.Sync.SyncStatus.syncInfo02');
  };

  // Handle change sync type
  handleSelectSyncType = value => {
    const { getSyncProcessStatus } = this.props;
    this.setState({ syncType: value });

    // Call to get new process status
    clearTimeout(this.timeoutGetSyncProcessStatus);
    getSyncProcessStatus(value);
  };

  handleChangeModal = stateField => {
    const value = this.state[stateField] || false;
    this.setState({ [stateField]: !value });
  };

  // Handle manual sync button
  triggerSync = async () => {
    const { triggerSync, getSyncProcessStatus, trackEvent } = this.props;
    const { syncType } = this.state;

    // Disable button
    this.setState({ triggerSyncFlag: true, activeConfirmSyncModal: false, showProgress: true });
    clearTimeout(this.timeoutGetSyncProcessStatus);

    await triggerSync(syncType);
    await getSyncProcessStatus(syncType);
    trackEvent('csatFeedback', 'syncRecentUpdate', 'csatFeedback_syncRecentUpdate');

    trackEvent('activeCampaign', 'sync_recent_update', 'activeCampaign_sync_recent_update');
  };

  openLink = (id: string) => {
    const newUrl = URL[id];
    window.open(newUrl, '_blank');
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  /** Subscribe addon */
  handleCloseStatus = () => {
    this.props.getAddonStatus(SYNC_FREQUENCY_NAME);
  };
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const syncSchedule = _.get(state.sync, 'syncSchedule', {});
  const flow = _.get(state.sync, 'flow', {});

  return {
    tenantId: _.get(state.oauth.tenantApp, 'tenantId', ''),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    addonStatus: _.get(state.subscriptionV3, `addonStatus.${SYNC_FREQUENCY_NAME}`, {}),
    statusStore: _.get(state.oauth, 'subscription.status', ''),
    subscriptionV3: _.get(tenantApp, 'settings.subscriptionV3.settings.enable', true),
    triggerSyncLoading: _.get(state.sync, 'triggerSyncLoading', false),
    error: _.get(state.sync, 'error', undefined),
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    syncProcessLoading: _.get(state.sync, 'syncProcessLoading', false),
    flow,
    syncProcess: _.get(state.sync, 'syncProcess', {}),
    availableSyncList: _.get(state.sync, 'availableSyncList', []),
    collectionBoostAllExists: _.get(state.sync, 'collectionBoostAllExists', false),
    emailSupport: _.get(state.oauth, 'emailSupport'),
    syncSchedule,
    enabledSubscriptionV4: _.get(
      state.oauth.tenantApp,
      'settings.subscriptionV4.settings.enable',
      false
    ),
    getSyncHistoryLoading: _.get(state.sync, 'getSyncHistoryLoading', false),
    getSyncScheduleLoading: _.get(state.sync, 'getSyncScheduleLoading', false),
    syncHistory: _.get(state.sync, 'syncHistory', [])
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get sync process status
  getSyncProcessStatus: (flowName = '') => dispatch(syncActions.getSyncProcessStatus(flowName)),
  // Get current status addon sync
  getAddonStatus: addonName => dispatch(subscriptionV3Actions.getAddonStatus(addonName)),
  // Call manual sync
  triggerSync: flowName => dispatch(syncActions.triggerSync(flowName, ownProps.i18n)),
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(StatusSyncStatus));
